import {Component, inject, OnInit} from '@angular/core';
import {NgClass, SlicePipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {RouterLink} from '@angular/router';
import {AdminService} from '@core/services/admin.service';
import {StatusAcesso, UsuarioPerfil, UsuarioSisnot} from '@core/models/usuario-sisnot';
import {MessageService} from 'primeng/api';
import {Button} from 'primeng/button';
import {Dialog} from 'primeng/dialog';
import {Select} from 'primeng/select';
import {Tag} from 'primeng/tag';
import {InputText} from 'primeng/inputtext';
import {Skeleton} from 'primeng/skeleton';
import {Toast} from 'primeng/toast';
import {IconField} from 'primeng/iconfield';
import {InputIcon} from 'primeng/inputicon';
import {Tooltip} from 'primeng/tooltip';
import {Card} from 'primeng/card';
import {Checkbox} from 'primeng/checkbox';

@Component({
  selector: 'app-admin-usuarios',
  imports: [
    NgClass, FormsModule, RouterLink, SlicePipe,
    Button, Dialog, Select, Tag, InputText, Skeleton, Toast,
    IconField, InputIcon, Tooltip, Card, Checkbox
  ],
  providers: [MessageService],
  templateUrl: './admin-usuarios.html',
})
export class AdminUsuarios implements OnInit {
  usuarios: UsuarioSisnot[] = [];
  filtrados: UsuarioSisnot[] = [];
  loading = true;
  filtroAtivo: StatusAcesso | 'TODOS' = 'TODOS';
  termoBusca = '';

  modalAberto = false;
  usuarioEditando: UsuarioSisnot | null = null;
  perfilSelecionado: UsuarioPerfil = 'VISUALIZADOR';
  statusSelecionado: StatusAcesso = 'PENDENTE';
  integracaoSeiSelecionada = false;
  salvando = false;

  readonly opcoesStatus = [
    {label: 'Pendente', value: 'PENDENTE'},
    {label: 'Aprovado', value: 'APROVADO'},
    {label: 'Recusado', value: 'RECUSADO'},
  ];

  readonly opcoesPerfil = [
    {label: 'Administrador', value: 'ADMIN'},
    {label: 'Usuário Avançado', value: 'ADV_USER'},
    {label: 'Visualizador', value: 'VISUALIZADOR'},
  ];

  private adminService = inject(AdminService);
  private messageService = inject(MessageService);

  ngOnInit() {
    this.carregarUsuarios();
  }

  carregarUsuarios() {
    this.loading = true;
    this.adminService.listarUsuarios().subscribe({
      next: lista => {
        this.usuarios = lista;
        this.aplicarFiltro();
        this.loading = false;
      },
      error: () => {
        this.messageService.add({severity: 'error', summary: 'Erro', detail: 'Não foi possível carregar os usuários'});
        this.loading = false;
      }
    });
  }

  filtrar(status: string) {
    this.filtroAtivo = status as StatusAcesso | 'TODOS';
    this.aplicarFiltro();
  }

  aplicarFiltro() {
    let lista = this.usuarios;
    if (this.filtroAtivo !== 'TODOS') {
      lista = lista.filter(u => u.statusAcesso === this.filtroAtivo);
    }
    if (this.termoBusca.trim()) {
      const termo = this.termoBusca.toLowerCase();
      lista = lista.filter(u =>
        u.username.toLowerCase().includes(termo) ||
        (u.nome ?? '').toLowerCase().includes(termo) ||
        (u.email ?? '').toLowerCase().includes(termo)
      );
    }
    this.filtrados = lista;
  }

  abrirModal(usuario: UsuarioSisnot) {
    this.usuarioEditando = usuario;
    this.perfilSelecionado = usuario.perfil;
    this.statusSelecionado = usuario.statusAcesso;
    this.integracaoSeiSelecionada = usuario.integracaoSei;
    this.modalAberto = true;
  }

  salvar() {
    if (!this.usuarioEditando) return;
    this.salvando = true;
    this.adminService.atualizarUsuario(this.usuarioEditando.id, {
      perfil: this.perfilSelecionado,
      statusAcesso: this.statusSelecionado,
      integracaoSei: this.integracaoSeiSelecionada,
    }).subscribe({
      next: atualizado => {
        const idx = this.usuarios.findIndex(u => u.id === atualizado.id);
        if (idx >= 0) this.usuarios[idx] = atualizado;
        this.aplicarFiltro();
        this.modalAberto = false;
        this.salvando = false;
        this.messageService.add({severity: 'success', summary: 'Salvo', detail: 'Usuário atualizado com sucesso'});
      },
      error: () => {
        this.salvando = false;
        this.messageService.add({severity: 'error', summary: 'Erro', detail: 'Não foi possível salvar'});
      }
    });
  }

  statusSeverity(status: StatusAcesso): 'warn' | 'success' | 'danger' | 'secondary' {
    return status === 'APROVADO' ? 'success' : status === 'RECUSADO' ? 'danger' : 'warn';
  }

  statusLabel(status: StatusAcesso): string {
    return status === 'APROVADO' ? 'Aprovado' : status === 'RECUSADO' ? 'Recusado' : 'Pendente';
  }

  perfilLabel(perfil: UsuarioPerfil): string {
    return perfil === 'ADMIN' ? 'Administrador' : perfil === 'ADV_USER' ? 'Avançado' : 'Visualizador';
  }

  perfilSeverity(perfil: UsuarioPerfil): 'danger' | 'info' | 'secondary' {
    return perfil === 'ADMIN' ? 'danger' : perfil === 'ADV_USER' ? 'info' : 'secondary';
  }

  totalPorStatus(status: string): number {
    if (status === 'TODOS') return this.usuarios.length;
    return this.usuarios.filter(u => u.statusAcesso === status as StatusAcesso).length;
  }
}
